/**
 * features/finance/PurchaseOrders.tsx
 * ------------------------------------------------------------------
 * ORDER IT, RECEIVE IT, THEN MATCH IT BEFORE PAYING.
 *
 * Until now a voucher was the first record that the school had committed
 * money — which is backwards. By the time somebody raises an instruction
 * to pay, the goods have arrived and the price is whatever the supplier's
 * invoice says. Every control the vouchers carry (three people, two bank
 * snapshots) protects the payment; none of them protects the purchase.
 *
 * THE SCREEN IS BUILT AROUND THE MATCH, NOT AROUND THE ORDER, because the
 * match is the reason the order exists. Opening an order answers "may this
 * invoice be paid, and if not, precisely why not" — not "what did we ask
 * for". Ordered, received and invoiced sit on the same row.
 *
 * THE VERDICT IS COMPUTED, NEVER STORED, the same rule the ledger follows.
 * A stored verdict is one that can be true when it was written and wrong
 * when it is read, which is the worst state a control can be in.
 *
 * A BLOCKED ORDER SAYS WHY IN WORDS. "60 still outstanding" is a morning's
 * work; a red icon is something to be clicked past.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import {
  ClipboardList, Plus, Send, PackageCheck, AlertTriangle, CheckCircle2,
  X, ArrowLeft, Ban, Trash2,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import {
  PO_COLLECTIONS, PO_STATUS_LABEL, MATCH_VERDICT_LABEL, DEFAULT_MATCH_TOLERANCE,
  matchPurchaseOrder, isPayable, poLinesTotal, normalisePoLine,
  type PurchaseOrder, type GoodsReceipt, type PoLine, type MatchVerdict,
} from '@/lib/purchaseOrders';
import {
  createPurchaseOrder, issuePo, cancelPo, closePo, recordGoodsReceipt,
} from '@/features/finance/poWrite';
import { VOUCHER_COLLECTIONS, type Voucher } from '@/lib/vouchers';
import { SUPPLIER_COLLECTION, type Supplier } from '@/lib/suppliers';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
  modalBackdrop, modalSurface,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const VERDICT_TONE: Record<MatchVerdict, string> = {
  'not-received': 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  'part-received': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  'ready-to-pay': 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  'over-delivered': 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  'over-invoiced': 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  'under-invoiced': 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  settled: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const blankLine = (): PoLine =>
  ({ description: '', qty: 1, unitCost: 0, lineTotal: 0 });

export default function PurchaseOrders() {
  const { userData } = useAuth();
  const [orders, setOrders] = useState<PurchaseOrder[] | null>(null);
  const [receipts, setReceipts] = useState<GoodsReceipt[]>([]);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => onSnapshot(
    query(collection(db, PO_COLLECTIONS.orders)),
    (s) => setOrders(s.docs.map((d) => ({ id: d.id, ...d.data() }) as PurchaseOrder)),
    () => setOrders([]),
  ), []);
  useEffect(() => onSnapshot(
    query(collection(db, PO_COLLECTIONS.receipts)),
    (s) => setReceipts(s.docs.map((d) => ({ id: d.id, ...d.data() }) as GoodsReceipt)),
    () => setReceipts([]),
  ), []);
  useEffect(() => onSnapshot(
    query(collection(db, VOUCHER_COLLECTIONS.vouchers)),
    (s) => setVouchers(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Voucher)),
    () => setVouchers([]),
  ), []);
  useEffect(() => onSnapshot(
    query(collection(db, SUPPLIER_COLLECTION)),
    (s) => setSuppliers(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Supplier)),
    () => setSuppliers([]),
  ), []);

  /* What the supplier has actually billed against each order. Cancelled and
     rejected vouchers are not bills — counting them would make an order look
     over-invoiced because somebody once typed it wrongly and withdrew it. */
  const invoicedFor = useMemo(() => {
    const by = new Map<string, number>();
    for (const v of vouchers) {
      if (['cancelled', 'rejected', 'draft'].includes(v.currentStatus)) continue;
      const poId = v.source?.type === 'purchase_order' ? v.source.id : undefined;
      if (!poId) continue;
      by.set(poId, (by.get(poId) ?? 0) + (Number(v.total) || 0));
    }
    return by;
  }, [vouchers]);

  const rows = useMemo(() => (orders ?? []).map((po) => {
    const mine = receipts.filter((g) => g.poId === po.id);
    const match = matchPurchaseOrder(po, mine, invoicedFor.get(po.id) ?? 0, DEFAULT_MATCH_TOLERANCE);
    return { po, receipts: mine, match };
  }).sort((a, b) => (b.po.date ?? '').localeCompare(a.po.date ?? '')), [orders, receipts, invoicedFor]);

  const open = rows.find((r) => r.po.id === openId);

  const live = rows.filter((r) => !['cancelled', 'closed'].includes(r.po.status));
  const blocked = live.filter((r) => !isPayable(r.match));
  const committed = live
    .filter((r) => r.po.status !== 'draft')
    .reduce((a, r) => a + (Number(r.po.total) || 0), 0);

  if (orders === null) return <div className="p-6 flex justify-center"><Spinner /></div>;

  if (open) {
    return (
      <OrderDetail
        row={open}
        suppliers={suppliers}
        onBack={() => setOpenId(null)}
        onError={setErr}
        actor={{ uid: userData?.uid ?? '', name: userData?.displayName }}
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={ClipboardList}
        tone="navy"
        title="Purchase Orders"
        subtitle="What was ordered, what arrived, and whether the invoice may be paid"
        actions={(
          <button type="button" className={btnPrimary} onClick={() => setCreating(true)}>
            <Plus size={15} /> New order
          </button>
        )}
      />

      {err && (
        <SoftCard className="mb-4 border-l-4 border-red-400">
          <p className="text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />{err}
          </p>
        </SoftCard>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatChip tone="navy" icon={ClipboardList} value={String(live.length)} label="Live orders" />
        <StatChip tone="slate" value={money(committed)} label="Committed to suppliers" />
        <StatChip
          tone={blocked.length > 0 ? 'rose' : 'emerald'}
          icon={blocked.length > 0 ? AlertTriangle : CheckCircle2}
          value={String(blocked.length)}
          label="Blocked from payment"
        />
      </div>

      <SoftCard pad="p-0">
        <PanelHeader icon={ClipboardList} title="Orders" tone="navy"
          subtitle="Newest first — open one to receive goods or read the match" />

        {rows.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">
            No purchase orders yet. Raising one before the money is committed is the
            point — it gives the invoice something to be checked against.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">Order</th>
                  <th className="px-2 font-semibold">Supplier</th>
                  <th className="px-2 font-semibold text-right">Ordered</th>
                  <th className="px-2 font-semibold text-right">Received</th>
                  <th className="px-2 font-semibold text-right">Invoiced</th>
                  <th className="px-2 pr-4 font-semibold">Match</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ po, match }) => (
                  <tr
                    key={po.id}
                    onClick={() => setOpenId(po.id)}
                    className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer"
                  >
                    <td className="py-2.5 pl-4 pr-2">
                      <span className="font-mono text-xs text-gray-600 dark:text-gray-300">{po.poNo}</span>
                      <span className="block text-[11px] text-gray-400">
                        {po.date} · {PO_STATUS_LABEL[po.status]}
                      </span>
                    </td>
                    <td className="px-2 text-gray-800 dark:text-gray-100 truncate max-w-[200px]">
                      {po.supplierName}
                    </td>
                    <td className="px-2 text-right tabular-nums">{money(match.ordered)}</td>
                    <td className="px-2 text-right tabular-nums">{money(match.received)}</td>
                    <td className="px-2 text-right tabular-nums">
                      {match.invoiced > 0 ? money(match.invoiced) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-2 pr-4">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${VERDICT_TONE[match.verdict]}`}>
                        {MATCH_VERDICT_LABEL[match.verdict]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SoftCard>

      {creating && (
        <NewOrderModal
          suppliers={suppliers}
          onClose={() => setCreating(false)}
          onError={setErr}
          actor={{ uid: userData?.uid ?? '', name: userData?.displayName }}
        />
      )}
    </div>
  );
}

/* ------------------------------- detail ------------------------------- */

function OrderDetail({
  row, onBack, onError, actor,
}: {
  row: { po: PurchaseOrder; receipts: GoodsReceipt[]; match: ReturnType<typeof matchPurchaseOrder> };
  suppliers: Supplier[];
  onBack: () => void;
  onError: (m: string | null) => void;
  actor: { uid: string; name?: string };
}) {
  const { po, receipts, match } = row;
  const [receiving, setReceiving] = useState(false);
  const [busy, setBusy] = useState(false);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    onError(null);
    try { await fn(); } catch (e) { onError((e as Error).message); }
    setBusy(false);
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <button type="button" onClick={onBack} className={`${btnGhost} mb-4`}>
        <ArrowLeft size={15} /> All orders
      </button>

      <PageHeader
        icon={ClipboardList}
        tone="navy"
        title={po.poNo}
        subtitle={`${po.supplierName} · ${PO_STATUS_LABEL[po.status]}`}
        actions={(
          <div className="flex gap-2">
            {po.status === 'draft' && (
              <button type="button" disabled={busy} className={btnPrimary}
                onClick={() => act(() => issuePo(po))}>
                <Send size={15} /> Issue to supplier
              </button>
            )}
            {['issued', 'part_received'].includes(po.status) && (
              <button type="button" className={btnPrimary} onClick={() => setReceiving(true)}>
                <PackageCheck size={15} /> Receive goods
              </button>
            )}
            {po.status === 'received' && (
              <button type="button" disabled={busy} className={btnGhost}
                onClick={() => act(() => closePo(po))}>
                Close order
              </button>
            )}
            {!['cancelled', 'closed'].includes(po.status) && (
              <button type="button" disabled={busy} className={btnGhost}
                onClick={() => {
                  const reason = window.prompt('Why is this order being cancelled?');
                  if (reason) act(() => cancelPo(po, reason, receipts));
                }}>
                <Ban size={15} /> Cancel
              </button>
            )}
          </div>
        )}
      />

      {/* The verdict, first, in words. Everything below is the evidence for it. */}
      <SoftCard className={`mb-5 border-l-4 ${isPayable(match) ? 'border-emerald-400' : 'border-red-400'}`}>
        <div className="flex items-start gap-3">
          {isPayable(match)
            ? <CheckCircle2 size={20} className="text-emerald-600 mt-0.5 shrink-0" />
            : <AlertTriangle size={20} className="text-red-600 mt-0.5 shrink-0" />}
          <div>
            <p className="font-bold text-gray-900 dark:text-white">
              {MATCH_VERDICT_LABEL[match.verdict]}
            </p>
            {match.issues.length > 0 ? (
              <ul className="mt-1 space-y-0.5 text-sm text-gray-600 dark:text-gray-300">
                {match.issues.map((i) => <li key={i}>· {i}</li>)}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 mt-0.5">
                Ordered, received and invoiced agree within tolerance.
              </p>
            )}
          </div>
        </div>
      </SoftCard>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatChip tone="slate" value={money(match.ordered)} label="Ordered" />
        <StatChip tone="navy" value={money(match.received)} label="Value received" />
        <StatChip
          tone={match.invoiced > 0 && !match.withinTolerance ? 'rose' : 'slate'}
          value={money(match.invoiced)} label="Invoiced by the supplier" />
      </div>

      <SoftCard pad="p-0" className="mb-5">
        <PanelHeader icon={ClipboardList} title="Lines" tone="navy"
          subtitle="Ordered against received, per line — a total-only check hides a swap between two of them" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                <th className="py-2 pl-4 pr-2 font-semibold">Description</th>
                <th className="px-2 font-semibold text-right">Unit</th>
                <th className="px-2 font-semibold text-right">Ordered</th>
                <th className="px-2 font-semibold text-right">Received</th>
                <th className="px-2 font-semibold text-right">Outstanding</th>
                <th className="px-2 pr-4 font-semibold text-right">Value received</th>
              </tr>
            </thead>
            <tbody>
              {match.lines.map((l) => (
                <tr key={l.idx} className="border-b border-gray-50 dark:border-gray-800">
                  <td className="py-2 pl-4 pr-2 text-gray-800 dark:text-gray-100">{l.description}</td>
                  <td className="px-2 text-right tabular-nums text-gray-500">{money(l.unitCost)}</td>
                  <td className="px-2 text-right tabular-nums">{l.qtyOrdered}</td>
                  <td className={`px-2 text-right tabular-nums font-medium ${
                    l.overDelivered ? 'text-red-600' : l.shortDelivered ? 'text-amber-600' : ''
                  }`}>{l.qtyReceived}</td>
                  <td className="px-2 text-right tabular-nums text-gray-500">
                    {l.qtyOutstanding || ''}
                  </td>
                  <td className="px-2 pr-4 text-right tabular-nums">{money(l.valueReceived)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SoftCard>

      <SoftCard pad="p-0">
        <PanelHeader icon={PackageCheck} title="Deliveries" tone="navy"
          subtitle="Never edited — a correction is another receipt, so the history survives" />
        {receipts.length === 0 ? (
          <p className="p-5 text-sm text-gray-500">Nothing has been received against this order yet.</p>
        ) : (
          <div className="p-4 space-y-2">
            {receipts.map((g) => (
              <div key={g.id} className="flex items-center justify-between text-[13px] border-b border-gray-50 dark:border-gray-800 pb-2 last:border-0">
                <div>
                  <span className="font-mono text-xs text-gray-600 dark:text-gray-300">{g.grnNo}</span>
                  <span className="text-gray-500 ml-2">
                    {g.receivedDate} · {g.receivedByName ?? 'received'}
                    {g.deliveryRef ? ` · note ${g.deliveryRef}` : ''}
                  </span>
                </div>
                <span className="text-gray-600 dark:text-gray-300">
                  {g.lines.reduce((a, l) => a + l.qtyReceived, 0)} items
                </span>
              </div>
            ))}
          </div>
        )}
      </SoftCard>

      {receiving && (
        <ReceiveModal
          po={po}
          match={match}
          actor={actor}
          onClose={() => setReceiving(false)}
          onError={onError}
        />
      )}
    </div>
  );
}

/* ------------------------------- receive ------------------------------- */

function ReceiveModal({
  po, match, actor, onClose, onError,
}: {
  po: PurchaseOrder;
  match: ReturnType<typeof matchPurchaseOrder>;
  actor: { uid: string; name?: string };
  onClose: () => void;
  onError: (m: string | null) => void;
}) {
  /* Defaults to what is still outstanding, because that is what usually
     arrives. A store-keeper correcting a pre-filled number is doing the
     check; one typing every number from scratch is doing data entry. */
  const [qty, setQty] = useState<Record<number, string>>(
    () => Object.fromEntries(match.lines.map((l) => [l.idx, String(l.qtyOutstanding)])),
  );
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    onError(null);
    try {
      await recordGoodsReceipt({
        po,
        lines: match.lines
          .map((l) => ({ poLineIdx: l.idx, qtyReceived: Number(qty[l.idx]) || 0 }))
          .filter((l) => l.qtyReceived !== 0),
        receivedDate: date,
        deliveryRef: ref.trim() || undefined,
        actor,
      });
      onClose();
    } catch (e) {
      onError((e as Error).message);
    }
    setBusy(false);
  }

  return (
    <div className={modalBackdrop} onClick={onClose}>
      <div className={modalSurface('max-w-2xl w-full')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-gray-900 dark:text-white">Receive against {po.poNo}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Date it arrived</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className={glassFieldClass('mt-1')} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Delivery note</span>
              <input value={ref} onChange={(e) => setRef(e.target.value)}
                placeholder="Supplier's waybill number"
                className={glassFieldClass('mt-1')} />
            </label>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                <th className="py-1 font-semibold">Item</th>
                <th className="font-semibold text-right w-20">Ordered</th>
                <th className="font-semibold text-right w-20">Already in</th>
                <th className="font-semibold text-right w-28">Arriving now</th>
              </tr>
            </thead>
            <tbody>
              {match.lines.map((l) => (
                <tr key={l.idx} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="py-1.5 text-gray-800 dark:text-gray-100">{l.description}</td>
                  <td className="text-right tabular-nums text-gray-500">{l.qtyOrdered}</td>
                  <td className="text-right tabular-nums text-gray-500">{l.qtyReceived}</td>
                  <td className="text-right">
                    <input
                      type="number" step="any"
                      value={qty[l.idx] ?? ''}
                      onChange={(e) => setQty({ ...qty, [l.idx]: e.target.value })}
                      className={glassFieldClass('text-right')}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Not refused — over-delivery happens and must be recordable. It
              simply cannot then be paid without somebody accepting the cost. */}
          <p className="text-[12px] text-gray-500">
            Record what actually arrived, even if it is more than was ordered. An
            over-delivery blocks payment until somebody accepts the extra cost, which
            is the point — it must not be settled by the goods quietly being kept.
          </p>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-gray-100 dark:border-gray-700">
          <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
          <button type="button" className={btnPrimary} disabled={busy} onClick={save}>
            {busy ? 'Recording…' : 'Record delivery'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- new order ------------------------------- */

function NewOrderModal({
  suppliers, onClose, onError, actor,
}: {
  suppliers: Supplier[];
  onClose: () => void;
  onError: (m: string | null) => void;
  actor: { uid: string; name?: string };
}) {
  const [supplierId, setSupplierId] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [expected, setExpected] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<PoLine[]>([blankLine()]);
  const [busy, setBusy] = useState(false);

  const priced = lines.map(normalisePoLine);
  const total = poLinesTotal(priced);

  function setLine(i: number, patch: Partial<PoLine>) {
    setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }

  async function save() {
    setBusy(true);
    onError(null);
    try {
      const chosen = suppliers.find((s) => s.id === supplierId);
      await createPurchaseOrder({
        supplierId: chosen?.id,
        supplierName: chosen?.name ?? supplierName,
        lines: priced,
        date,
        expectedDate: expected || undefined,
        notes: notes.trim() || undefined,
        actor,
      });
      onClose();
    } catch (e) {
      onError((e as Error).message);
    }
    setBusy(false);
  }

  return (
    <div className={modalBackdrop} onClick={onClose}>
      <div className={modalSurface('max-w-3xl w-full')} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-gray-900 dark:text-white">New purchase order</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3 max-h-[65vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block sm:col-span-1">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Supplier</span>
              <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}
                className={glassFieldClass('mt-1')}>
                <option value="">— not in the register —</option>
                {suppliers.filter((s) => s.active !== false).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>
            {!supplierId && (
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-gray-400">Name</span>
                <input value={supplierName} onChange={(e) => setSupplierName(e.target.value)}
                  className={glassFieldClass('mt-1')} placeholder="Who the order goes to" />
              </label>
            )}
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Order date</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className={glassFieldClass('mt-1')} />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-gray-400">Expected</span>
              <input type="date" value={expected} onChange={(e) => setExpected(e.target.value)}
                className={glassFieldClass('mt-1')} />
            </label>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                <th className="py-1 font-semibold">Description</th>
                <th className="font-semibold text-right w-20">Qty</th>
                <th className="font-semibold text-right w-28">Unit price</th>
                <th className="font-semibold text-right w-28">Line total</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="py-1.5 pr-2">
                    <input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })}
                      className={glassFieldClass()} placeholder="What is being bought" />
                  </td>
                  <td className="pr-2">
                    <input type="number" step="any" value={l.qty}
                      onChange={(e) => setLine(i, { qty: Number(e.target.value) })}
                      className={glassFieldClass('text-right')} />
                  </td>
                  <td className="pr-2">
                    <input type="number" step="0.01" value={l.unitCost}
                      onChange={(e) => setLine(i, { unitCost: Number(e.target.value) })}
                      className={glassFieldClass('text-right')} />
                  </td>
                  <td className="pr-2 text-right tabular-nums text-gray-600 dark:text-gray-300">
                    {money(priced[i].lineTotal)}
                  </td>
                  <td>
                    {lines.length > 1 && (
                      <button type="button" className="text-gray-400 hover:text-red-600"
                        onClick={() => setLines(lines.filter((_, idx) => idx !== i))}>
                        <Trash2 size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button type="button" className={btnGhost} onClick={() => setLines([...lines, blankLine()])}>
            <Plus size={14} /> Add a line
          </button>

          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-gray-400">Notes</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className={glassFieldClass('mt-1')} placeholder="Anything the supplier needs told" />
          </label>
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-gray-100 dark:border-gray-700">
          <span className="text-sm text-gray-500">
            Order total <strong className="text-gray-900 dark:text-white tabular-nums">{money(total)}</strong>
          </span>
          <div className="flex gap-2">
            <button type="button" className={btnGhost} onClick={onClose}>Cancel</button>
            <button type="button" className={btnPrimary} disabled={busy} onClick={save}>
              {busy ? 'Saving…' : 'Save as draft'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
